import { CommonUIError } from '@afterpay/types'
import clsx from 'clsx'
import React, { ChangeEvent, FocusEvent, forwardRef, isValidElement, KeyboardEvent, ReactNode, useEffect, useImperativeHandle, useRef, useState } from 'react'

import { Icon } from '../Icon'
import { InputSizes } from '../Input'
import { DesignLanguage, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Span } from '../Typography'
import { noop } from '../utils/function'
import { KeyCodes } from '../utils/keyboard'
import { getChildProps } from '../utils/react'
import { MENU_ITEM_DISPLAY_NAME } from './constants'
import { Menu } from './Menu'
import { MenuItem, MenuItemProps } from './MenuItem'
import styles from './style.module.scss'

export interface SelectProps extends CommonUIError {
  children?: ReactNode
  defaultValue?: string
  disabled?: boolean
  id?: string
  autoComplete?: string
  label?: string
  name?: string
  onBlur?: (event: FocusEvent<HTMLSelectElement | HTMLButtonElement | HTMLUListElement>) => any
  onSelect?: (value: string) => any
  scrollIntoView?: boolean
  size?: InputSizes
  testNameSpace?: string
  disableNativeLabel?: boolean
  infoText?: string
  'aria-labelledby'?: string
  'aria-describedby'?: string
}

const defaultID = Math.random().toString(36)

export const Select = forwardRef<any, SelectProps>(
  (
    {
      children,
      defaultValue = '',
      disabled = false,
      error = false,
      errorText = '',
      id = defaultID,
      label = '',
      name = '',
      onBlur = noop,
      onSelect = noop,
      scrollIntoView = true,
      size = 'Large',
      testNameSpace = '',
      disableNativeLabel = false,
      infoText = '',
      ...otherProps
    },
    ref
  ) => {
    if (typeof children === 'undefined') {
      return null
    }

    /* eslint-disable react-hooks/rules-of-hooks */
    const [expanded, setExpanded] = useState(false)
    const [value, setValue] = useState(defaultValue)
    const componentRef = useRef<HTMLDivElement>(null)
    const selectRef = useRef<HTMLSelectElement>(null)
    const customButtonRef = useRef<HTMLButtonElement>(null)
    const themeClass = useThemeClass()
    const isArcadeDesign = useIsDesignLanguage(DesignLanguage.Arcade)

    useEffect(() => {
      if (expanded && scrollIntoView) {
        componentRef?.current?.scrollIntoView({ behavior: 'smooth' })
      }
      return () => {}
    }, [expanded, scrollIntoView])

    useImperativeHandle(ref, () => ({
      focus: () => {
        const customButton = customButtonRef.current
        const customButtonVisible = Boolean(customButton?.offsetWidth ?? customButton?.offsetHeight ?? customButton?.getClientRects().length)
        const elementToFocus = customButtonVisible ? customButton : selectRef.current
        elementToFocus?.focus()
      },
    }))

    useEffect(() => {
      const handleClickOutside = (e: MouseEvent): void => {
        if (e.target instanceof HTMLElement && componentRef.current !== null && !componentRef.current.contains(e.target)) {
          setExpanded(false)
        }
      }

      document.addEventListener('mouseup', handleClickOutside)
      return () => {
        document.removeEventListener('mouseup', handleClickOutside)
      }
    }, [onBlur])

    /* eslint-enable react-hooks/rules-of-hooks */

    const menuId = `${id}-dropdown-menu`
    const childrenList = React.Children.toArray(children)
    const childrenMenuProps = childrenList
      .filter((child) => {
        // @ts-expect-error
        return child.type.displayName === MENU_ITEM_DISPLAY_NAME
      })
      .map((child) => getChildProps(child))
    const selectedChild = childrenMenuProps.find((child) => child.value === value)

    const handleDropdownClick = (): void => {
      setExpanded(!expanded)
    }

    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>): void => {
      const newValue = e.target.value
      setValue(newValue)

      onSelect(newValue)
    }

    const handleMenuItemClick = (value: string): void => {
      setValue(value)
      setExpanded(false)

      onSelect(value)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>): void => {
      if (disabled) {
        return
      }

      const { key } = e
      if (key === KeyCodes.Escape) {
        e.preventDefault()
        setExpanded(false)
      }
    }

    const handleMenuBlur = (e: FocusEvent<HTMLUListElement>): void => {
      const nextFocusTarget = e.relatedTarget?.parentElement

      if (!(componentRef.current?.contains(nextFocusTarget ?? null) ?? false)) {
        setExpanded(false)
        onBlur(e)
      }
    }

    const renderLabel = (className: string = styles.label): ReactNode => {
      const labelClassName = clsx(className, disabled && isArcadeDesign && styles.disabledLabel, styles[themeClass])
      return (
        <label className={labelClassName} {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-label` })}>
          {label}
        </label>
      )
    }

    const nativeSelectStyle = clsx(
      styles.select,
      value && styles.selectedNativeValue,
      disabled && styles.disabled,
      styles[`size${size}`],
      error && styles.error,
      styles[`${themeClass}`]
    )

    const customSelectStyle = clsx(styles.customSelect, disabled && styles.disabled, styles[themeClass])

    const customSelectTriggerButtonStyle = clsx(
      styles.customTriggerButton,
      expanded && styles.expanded,
      disabled && styles.disabled,
      styles[`size${size}`],
      error && styles.error,
      value !== '' && styles.filled,
      styles[`${themeClass}`]
    )

    const selectedInputValueStyle = clsx(label !== '' && size === 'Large' && styles.selectedValueWithLabel, styles[themeClass], disabled && styles.disabled)

    const renderCustomMenus = (): ReactNode => {
      return childrenList.map((child, index) => {
        if (!isValidElement(child)) {
          return null
        }

        // @ts-expect-error
        const isMenuItem = child.type.displayName === MENU_ITEM_DISPLAY_NAME

        if (!isMenuItem) {
          return child
        }

        const childProps: MenuItemProps = getChildProps(child)
        const childId = `${id}_${childProps.value ?? index}`
        const selected = childProps.value === value
        return (
          <MenuItem
            key={childId}
            id={childId}
            selected={selected}
            onClick={handleMenuItemClick}
            {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-dropdown-menu-item` })}
            {...childProps}
          />
        )
      })
    }

    const displayFloatingLabel = !isArcadeDesign && label !== ''
    const displayStaticLabel = isArcadeDesign && label !== ''
    const dropdownWrapperStyles = clsx(styles.dropdownWrapper, styles[themeClass])
    const staticLabelStyles = clsx(styles.label, styles.staticLabel)

    const renderCustomSelect = (): ReactNode => {
      return (
        <>
          <button
            type='button'
            className={customSelectTriggerButtonStyle}
            onClick={handleDropdownClick}
            onKeyDown={handleKeyDown}
            name={name}
            tabIndex={-1}
            ref={customButtonRef}
            {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-triggerButton` })}
          >
            {displayFloatingLabel && renderLabel()}
            <span className={selectedInputValueStyle}>{selectedChild?.children}</span>
            <span className={styles.iconWrapper}>
              <Icon.CaretDown size='ExtraSmall' title={label} color={disabled ? 'Gray80' : 'Black'} />
            </span>
          </button>
          {expanded && (
            <div className={styles.menuWrapper}>
              <Menu
                onBlur={(e) => handleMenuBlur(e)}
                defaultValue={value}
                id={menuId}
                {...(testNameSpace.length > 0 && { testNameSpace: `${testNameSpace}-menu` })}
              >
                {renderCustomMenus()}
              </Menu>
            </div>
          )}
        </>
      )
    }

    const renderOptionChild = (children: ReactNode | ReactNode[]): string | null => {
      if (Array.isArray(children)) {
        return children.map((child) => (typeof child === 'string' ? child : renderOptionChild(getChildProps(child).children))).join('')
      }
      if (typeof children === 'string') {
        return children
      }

      return null
    }

    const renderNativeSelect = (): ReactNode => (
      <select
        className={nativeSelectStyle}
        value={value}
        onChange={handleSelectChange}
        onBlur={onBlur}
        aria-disabled={disabled}
        aria-expanded={expanded}
        id={id}
        name={name}
        ref={selectRef}
        {...(testNameSpace.length > 0 && { 'data-testid': `${testNameSpace}-input` })}
        {...(label.length > 0 && { 'aria-label': label })}
        {...otherProps}
      >
        <>
          {label !== '' ? (
            <option disabled={disableNativeLabel} value=''>
              {label}
            </option>
          ) : null}
          {childrenMenuProps.map((childProps, index) => {
            const { value, children } = childProps
            const childId = `${id}_${(value as string) ?? index}`
            return (
              <option key={childId} value={value}>
                {renderOptionChild(children)}
              </option>
            )
          })}
        </>
      </select>
    )

    return (
      <>
        {displayStaticLabel && renderLabel(staticLabelStyles)}
        <div className={dropdownWrapperStyles} ref={componentRef}>
          {renderNativeSelect()}
          <div className={customSelectStyle} aria-hidden='true'>
            {renderCustomSelect()}
          </div>
          {typeof infoText === 'string' && infoText.length > 0 && (
            <div className={styles.errorText}>
              <Span color='Gray40' size='XXS'>
                {infoText}
              </Span>
            </div>
          )}
          {typeof errorText === 'string' && errorText.length > 0 && (
            <div className={styles.errorText}>
              <Span color='Fire' size='XS'>
                {errorText}
              </Span>
            </div>
          )}
        </div>
      </>
    )
  }
)

Select.displayName = 'Dropdown'
